import { useState } from 'react';
import { useCollection } from '@/hooks/useCollection';
import { createRow } from '@/lib/api';
import type { MediaItem } from '@/lib/types';

interface MediaPickerProps {
  onAdded: () => void;
}

export default function MediaPicker({ onAdded }: MediaPickerProps) {
  const { rows: media, loading } = useCollection<MediaItem>('media_library', 'created_at', false);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<string[]>([]);
  const [category, setCategory] = useState('');
  const [status, setStatus] = useState<'idle' | 'saving' | 'done' | 'error'>('idle');
  const [message, setMessage] = useState('');

  const toggle = (url: string) =>
    setSelected((prev) => (prev.includes(url) ? prev.filter((u) => u !== url) : [...prev, url]));

  const addSelected = async () => {
    if (selected.length === 0) return;
    setStatus('saving');
    setMessage('');
    try {
      let done = 0;
      for (const url of selected) {
        const match = media.find((m) => m.url === url);
        await createRow('gallery_images', {
          src: url,
          alt: match?.title || 'Gallery photo',
          title: match?.title || 'Gallery photo',
          description: '',
          category: category.trim() || match?.category || 'Worship',
          sort_order: 0,
          is_published: true,
        });
        done += 1;
      }
      setStatus('done');
      setMessage(`${done} photo${done > 1 ? 's' : ''} added from your media library.`);
      setSelected([]);
      onAdded();
    } catch (err) {
      setStatus('error');
      setMessage(err instanceof Error ? err.message : 'Failed to add photos. Please try again.');
    }
  };

  return (
    <div className="bg-white rounded-lg border border-background-200 p-6 mb-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <i className="ri-folder-image-line text-primary-600" aria-hidden="true" />
            <h3 className="font-heading font-semibold text-base text-foreground-900">Reuse Photos from Media Library</h3>
          </div>
          <p className="text-sm text-foreground-500">
            Pick photos you already uploaded and add them to the gallery — no need to upload them again.
          </p>
        </div>
        <button
          type="button"
          onClick={() => { setOpen(true); setStatus('idle'); setMessage(''); }}
          className="btn-outline whitespace-nowrap cursor-pointer shrink-0"
        >
          <i className="ri-image-add-line" aria-hidden="true" />
          Choose from Media Library
        </button>
      </div>

      {message && (
        <p className={`mt-3 text-sm ${status === 'error' ? 'text-red-600' : 'text-accent-700'}`}>{message}</p>
      )}

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} aria-hidden="true" />
          <div className="relative bg-white rounded-xl w-full max-w-2xl max-h-[85vh] flex flex-col shadow-2xl">
            <div className="flex items-center justify-between px-6 py-4 border-b border-background-200">
              <h3 className="font-heading font-semibold text-lg text-foreground-900">Choose from Media Library</h3>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="p-2 rounded-md hover:bg-background-100 cursor-pointer"
                aria-label="Close"
              >
                <i className="ri-close-line text-xl" aria-hidden="true" />
              </button>
            </div>

            <div className="px-6 py-3 border-b border-background-100 flex flex-wrap items-center gap-3">
              <label className="text-xs font-label font-medium text-foreground-700">Category for added photos</label>
              <input
                type="text"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                placeholder="e.g. Worship"
                className="px-3.5 py-2 rounded-md text-sm border border-background-300 bg-white outline-none focus:ring-2 focus:ring-primary-300 text-foreground-900 w-48"
              />
              <span className="text-xs text-foreground-500 ml-auto">{selected.length} selected</span>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {loading ? (
                <p className="text-center text-foreground-400 py-10 text-sm">
                  <i className="ri-loader-4-line animate-spin inline-block" aria-hidden="true" /> Loading...
                </p>
              ) : media.length === 0 ? (
                <p className="text-center text-foreground-400 py-10 text-sm">
                  Your media library is empty. Upload photos in the Media Library section first.
                </p>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {media.map((item) => {
                    const isSel = selected.includes(item.url);
                    return (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => toggle(item.url)}
                        className={`group relative rounded-lg overflow-hidden border-2 transition-colors cursor-pointer text-left ${
                          isSel ? 'border-primary-500' : 'border-background-200 hover:border-primary-300'
                        }`}
                      >
                        <div className="h-28 w-full overflow-hidden">
                          <img src={item.url} alt={item.title || 'Library photo'} className="w-full h-full object-cover object-top" />
                        </div>
                        <p className="text-[11px] text-foreground-600 px-2 py-1.5 line-clamp-1">{item.title || 'Untitled'}</p>
                        {isSel && (
                          <span className="absolute top-2 right-2 w-6 h-6 flex items-center justify-center rounded-full bg-primary-500 text-white">
                            <i className="ri-check-line text-sm" aria-hidden="true" />
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="px-6 py-4 border-t border-background-200 flex justify-end gap-3">
              <button type="button" onClick={() => setOpen(false)} className="btn-outline whitespace-nowrap">
                Cancel
              </button>
              <button
                type="button"
                onClick={addSelected}
                disabled={selected.length === 0 || status === 'saving'}
                className="btn-primary whitespace-nowrap disabled:opacity-50 cursor-pointer"
              >
                {status === 'saving' ? 'Adding...' : `Add ${selected.length || ''} to Gallery`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}